import { memo, useEffect, useState } from 'react';
import type { NodeProps, Node } from '@xyflow/react';
import * as Y from 'yjs';
import styles from './component.module.css';
import fmtStyles from '../formatting.module.css';
import type { TextNodeData } from './types';
import { makeDefault } from './defaults';
import { withDefaults } from '../with-defaults';
import { SelectionTransformer } from '@/features/canvas/engine/selection/selection-transformer';
import transformerStyles from '@/features/canvas/engine/selection/selection-transformer.module.css';
import { useNodeActions } from "@/features/canvas/engine/wiring/contexts/node-actions-context";
import { useCanvasDoc } from "@/features/canvas/engine/wiring/contexts/canvas-doc-context";
import { formattingVars } from '../../hooks/use-formatting-state';
import { resolveNodeAriaLabel } from '../../a11y/aria-labels';
import { useParentLabelResolver } from '../../a11y/parent-label-context';
import { NodeTextEditor } from '../node-text-editor';

type TextNodeShape = Node<TextNodeData, 'text'>;

export default memo(function TextNode({ id, data, selected, dragging, parentId }: NodeProps<TextNodeShape>) {
  // Merge canonical defaults so legacy text nodes missing fontSize / color /
  // alignment fall back to the design-system value rather than rendering
  // undefined CSS. `text` stays the passed Y.Text and is normalised below.
  const d = withDefaults<TextNodeData>(data, makeDefault as unknown as () => TextNodeData);
  const { text: rawText, fontSize, fontFamily, color, textAlign, verticalAlign, bold, italic, underline, strikethrough } = d;
  const text = rawText instanceof Y.Text ? rawText : null;
  const actions = useNodeActions();
  const doc = useCanvasDoc();
  const [render, setRender] = useState(text?.toString() ?? '');
  // Start in edit mode if selected on mount (freshly placed via tool click).
  // Read-only is enforced in NodeTextEditor, so a RO canvas stays non-editable
  // even if this is true.
  const [editing, setEditing] = useState(!!selected);
  const parentLabel = useParentLabelResolver();
  const ariaLabel = resolveNodeAriaLabel(
    { type: 'text', data: { text: render }, parentId: parentId ?? null },
    parentLabel,
  );

  // Keep render state in sync for aria-label updates.
  useEffect(() => {
    if (!text) return;
    const refresh = () => setRender(text.toString());
    text.observe(refresh);
    return () => text.unobserve(refresh);
  }, [text]);

  // Exit edit mode when deselected.
  useEffect(() => {
    if (!selected) setEditing(false);
  }, [selected]);

  if (!text) return null;

  return (
    <SelectionTransformer
      nodeId={id}
      nodeType="text"
      selected={!!selected}
      dragging={!!dragging}
      editing={editing}
      onExpand={actions?.onExpand}
      doc={doc ?? undefined}
      className={transformerStyles.rootFit}
    >
      <div
        className={styles.text}
        data-testid="text-node"
        data-node-id={id}
        data-selected={selected || undefined}
        data-editing={editing || undefined}
        aria-label={ariaLabel}
        style={{
          '--text-color': color,
          ...formattingVars({ fontSize, fontFamily, textAlign, verticalAlign, bold, italic, underline, strikethrough, textColor: color }),
        } as React.CSSProperties}
        onClick={() => { if (selected) setEditing(true); }}
        onDoubleClick={() => setEditing(true)}
      >
        <NodeTextEditor
          ytext={text}
          editing={editing}
          ariaLabel="text node content"
          className={editing
            ? `${styles.editor} ${fmtStyles.formattingText} nodrag`
            : `${styles.editor} ${fmtStyles.formattingText}`}
          onBlur={() => {
            setEditing(false);
            if (text.toString().trim() === '') {
              actions?.onDelete(id);
            }
          }}
        />
      </div>
    </SelectionTransformer>
  );
});
